import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import * as BoostsSelectors from './boosts.selectors';
import * as BoostsActions from './boosts.actions';
import { Observable, filter, of, switchMap, tap } from 'rxjs';
import { OfferState } from './boosts.reducer';
import { WithRequiredProp } from '@woolworthsnz/styleguide';
import { TealiumUtagService } from '@woolworthsnz/analytics';

@Injectable({ providedIn: 'root' })
export class BoostsFacade {
	public hasOffers$ = this.store.select(BoostsSelectors.selectHasOffers);
	public boostOffers$ = this.store.select(BoostsSelectors.selectOffers);
	public productOffers$ = this.store.select(BoostsSelectors.selectProductOffers);
	public otherOffers$ = this.store.select(BoostsSelectors.selectOtherOffers);
	public isLoadingOffers$ = this.store.select(BoostsSelectors.selectIsLoadingOffers);
	public isAllBoosting$ = this.store.select(BoostsSelectors.selectIsAllBoosting);
	public isAllOffersBoosted$ = this.store.select(BoostsSelectors.selectIsAllOffersBoosted);
	public hasLoadedBoostOffers$ = this.store.select(BoostsSelectors.selectHasLoadedOffers);
	public activatedBoostOfferIdList$ = this.store.select(BoostsSelectors.selectActivatedBoostOfferIdList);

	constructor(private readonly store: Store, private tealiumUtagService: TealiumUtagService) {}

	public isBoostOfferActivated(offerId: string): Observable<boolean> {
		return this.store.select(BoostsSelectors.selectIsBoostOfferActivated(offerId));
	}

	public getOrFetchBoostOfferById(id: string): Observable<WithRequiredProp<OfferState, 'offer'> | undefined> {
		return this.getBoostOfferByIdFromStore(id).pipe(
			tap((offer) => {
				if (!offer) {
					this.store.dispatch(BoostsActions.loadBoostOffer({ id }));
				}
			}),
			switchMap((offer) =>
				offer
					? of(offer)
					: this.isLoadingOffers$.pipe(
							filter((isLoading) => !isLoading),
							switchMap(() => this.getBoostOfferByIdFromStore(id))
					  )
			)
		);
	}

	public loadBoostOffers(): void {
		this.store.dispatch(BoostsActions.loadBoostOffers());
	}

	public activateBoostOffer(id: string): void {
		this.store.dispatch(BoostsActions.activateBoostOffer({ id }));
		this.tealiumUtagService.link({
			tealium_event: 'select_content',
			content_type: 'CTA',
			content_name: 'Boost',
			content_text: 'Boost',
		});
	}

	public markOffersAsBoosted(activatedOfferIdList: string[]): void {
		this.store.dispatch(BoostsActions.markBoostOffersAsActivated({ activatedOfferIdList }));
	}

	public activateAllBoostOffers(): void {
		this.store.dispatch(BoostsActions.activateAllBoostOffers());
		this.tealiumUtagService.link({
			tealium_event: 'select_content',
			content_type: 'CTA',
			content_name: 'Boost',
			content_text: 'Boost all offers',
		});
	}

	private getBoostOfferByIdFromStore(id: string): Observable<WithRequiredProp<OfferState, 'offer'> | undefined> {
		return this.store.select(BoostsSelectors.selectOffer(id));
	}
}
